import bluebird from 'bluebird'
import yaml from 'js-yaml'
import CreateReducer from '../utils/createReducer'
import CreateActions from '../utils/createActions'

import {
  handlers,
} from '../utils/api'

import {
  validateConfig,
} from 'utils/bookingform'

import dateUtils from 'shared/src/date'
import calendarUtils from 'shared/src/calendar'
import networkWrapper from '../utils/networkWrapper'
import routerActions from './router'
import snackbarActions from './snackbar'
import bookingActions from './booking'
import projectActions from './project'
import projectSelectors from '../selectors/project'
import adminSelectors from '../selectors/admin'
import bookingformSelectors from '../selectors/bookingform'
import bookingformActions from './bookingform'

const prefix = 'admin'
const wrapper = networkWrapper.factory(prefix)

const initialState = {
  config: {},
  editSlotWindowOpen: false,
  emailAttachments: [],
  updateConfigsResult: '',
  searchResults: [],
  moveCalendar: [],
  moveCalendarConfig: null,
  calendarCondensed: localStorage.getItem('admin.calendarCondensed') ? true : false,
  calendarSlotFilter: localStorage.getItem('admin.calendarSlotFilter') ? localStorage.getItem('admin.calendarSlotFilter') : 'all',
}

const reducers = {
  setConfig: (state, action) => {
    state.config = action.payload
  },
  setEditSlotWindowOpen: (state, action) => {
    state.editSlotWindowOpen = action.payload
  },
  setEmailAttachments: (state, action) => {
    state.emailAttachments = action.payload
  },
  setUpdateConfigsResult: (state, action) => {
    state.updateConfigsResult = action.payload
  },
  setSearchResults: (state, action) => {
    state.searchResults = action.payload
  },
  setMoveCalendar: (state, action) => {
    state.moveCalendar = action.payload
  },
  setMoveCalendarConfig: (state, action) => {
    state.moveCalendarConfig = action.payload
  },
  setCalendarCondensed: (state, action) => {
    state.calendarCondensed = action.payload
    localStorage.setItem('admin.calendarCondensed', action.payload ? 'yes' : '')
  },
  setCalendarSlotFilter: (state, action) => {
    state.calendarSlotFilter = action.payload
    localStorage.setItem('admin.calendarSlotFilter', action.payload)
  },
}

const sideEffects = {

  loadConfig: () => wrapper('loadConfig', async (dispatch, getState) => {
    const data = await handlers.get(`/admin/config`)
    await dispatch(actions.setConfig(data))
  }, {
    globalLoading: false,
  }),

  updateProject: ({
    payload,
  }) => wrapper('updateProject', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.put(`/admin/projects/${project}`, payload)
    await dispatch(projectActions.loadAdminProjects())
    dispatch(snackbarActions.setSuccess(`settings updated`))
  }, {
    globalLoading: true,
  }),

  // ask the api what project the current booking form is for and switch
  // if it's different to the current project
  // this is to solve deep linking into a bookingform for a project
  // you are not currently looking at
  ensureBookingFormProject: ({
    id,
  }) => wrapper('ensureBookingFormProject', async (dispatch, getState) => {
    if(id == 'new') return true
    const currentProject = projectSelectors.currentProjectId(getState())
    const result = await handlers.get(`/admin/bookingform/${id}/projectid`)
    if(result.project != currentProject) await dispatch(projectActions.switchProject(result.project, false))
    return true
  }, {
    globalLoading: false,
  }),

  updateConfigs: () => wrapper('updateConfigs', async (dispatch, getState) => {
    const result = await handlers.post(`/admin/updateConfigs`, {})
    await dispatch(actions.setUpdateConfigsResult(result.data))
    dispatch(snackbarActions.setSuccess(`configs updated`))
  }, {
    globalLoading: true,
  }),

  saveBookingform: (id, payload) => wrapper('saveBookingform', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    const existingBookingForm = bookingformSelectors.editBookingForm(getState())
    const data = {
      meta: Object.assign({}, existingBookingForm ? existingBookingForm.meta || {} : {}, payload.meta || {}),
    }
    if(payload.name) data.name = payload.name
    if(payload.url) data.url = payload.url
    if(payload.yaml_config) {
      data.yaml_config = payload.yaml_config
      data.json_config = yaml.load(data.yaml_config)
      validateConfig(data.json_config)
    }

    if(id == 'new') {
      await handlers.post(`/admin/projects/${project}/bookingforms`, data)
    }
    else {
      await handlers.put(`/admin/projects/${project}/bookingforms/${id}`, data)
      await dispatch(bookingformActions.loadAdminBookingformConfigs(id))
    }
    await dispatch(bookingformActions.loadAdminBookingforms())
    dispatch(snackbarActions.setSuccess(`booking form ${id == 'new' ? 'created' : 'updated'}`))
    return true
  }),

  deleteBookingform: (id) => wrapper('deleteBookingform', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.delete(`/admin/projects/${project}/bookingforms/${id}`)
    await dispatch(bookingformActions.loadAdminBookingforms())
    dispatch(snackbarActions.setSuccess(`booking form deleted`))
    return true
  }),

  updateSlotOverrides: ({
    bookingform,
    date,
    block_id,
    slot_id,
    data,
  }) => wrapper('updateSlotOverrides', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.put(`/admin/projects/${project}/bookingforms/${bookingform}/slot/overrides/${date}/${block_id}/${slot_id}`, data)
    dispatch(bookingActions.loadCalendar({
      project,
      bookingform,
      date,
      days: 1,
      admin: true,
    }))
    dispatch(actions.setEditSlotWindowOpen(false))
    dispatch(snackbarActions.setSuccess(`slot updated`))
    return true
  }, {
    throwErrors: true,
  }),

  updateDayOverride: ({
    project,
    bookingform,
    date,
    data,
  }) => wrapper('updateDayOverride', async (dispatch, getState) => {
    await handlers.put(`/admin/projects/${project}/bookingforms/${bookingform}/day/overrides/${date}`, data)    
    dispatch(snackbarActions.setSuccess('day updated'))
    return true
  }, {
    globalLoading: true,
  }),

  resetSlotOverrides: ({
    bookingform,
    date,
    block_id,
    slot_id,
  }) => wrapper('resetSlotOverrides', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.delete(`/admin/projects/${project}/bookingforms/${bookingform}/slot/overrides/${date}/${block_id}/${slot_id}`)
    dispatch(bookingActions.loadCalendar({
      project,
      bookingform,
      date,
      days: 1,
      admin: true,
    }))
    dispatch(actions.setEditSlotWindowOpen(false))
    dispatch(snackbarActions.setSuccess(`slot updated`))
    return true
  }, {
    throwErrors: true,
  }),

  resetDayOverride: ({
    project,
    bookingform,
    date,
  }) => wrapper('resetDayOverride', async (dispatch, getState) => {
    await handlers.delete(`/admin/projects/${project}/bookingforms/${bookingform}/day/overrides/${date}`)
    dispatch(snackbarActions.setSuccess('day reset'))
    return true
  }, {
    globalLoading: true,
  }),

  updateSlotBlocked: ({
    bookingform,
    date,
    block_id,
    slot_id,
    data,
  }) => wrapper('updateSlotBlocked', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.put(`/admin/projects/${project}/bookingforms/${bookingform}/slot/blocked/${date}/${block_id}/${slot_id}`, data)
    dispatch(bookingActions.loadCalendar({
      project,
      bookingform,
      date,
      days: 1,
      admin: true,
    }))
    dispatch(actions.setEditSlotWindowOpen(false))
    dispatch(snackbarActions.setSuccess(`slot updated`))
    return true
  }, {
    throwErrors: true,
  }),

  updateSlotAdminOnly: ({
    bookingform,
    date,
    block_id,
    slot_id,
    data,
  }) => wrapper('updateSlotAdminOnly', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.put(`/admin/projects/${project}/bookingforms/${bookingform}/slot/admin_only/${date}/${block_id}/${slot_id}`, data)
    dispatch(bookingActions.loadCalendar({
      project,
      bookingform,
      date,
      days: 1,
      admin: true,
    }))
    dispatch(actions.setEditSlotWindowOpen(false))
    dispatch(snackbarActions.setSuccess(`slot updated`))
    return true
  }, {
    throwErrors: true,
  }),

  saveBooking: ({
    booking_id,
    bookingform,
    date,
    block_id,
    slot_id,
    data,
    updateSlot,
    communication,
  }) => wrapper('saveBooking', async (dispatch, getState) => {

    const project = projectSelectors.currentProjectId(getState())
    if(booking_id == 'new') {
      await handlers.post(`/admin/projects/${project}/bookingforms/${bookingform}/bookings`, {
        date,
        block_id,
        slot_id,
        booking: data,
        communication,
      })
      dispatch(snackbarActions.setSuccess(`booking created`))
    } else {
      await handlers.put(`/admin/projects/${project}/bookingforms/${bookingform}/bookings/${booking_id}`, {
        booking: data,
        updateSlot,
        communication,
      })
      dispatch(snackbarActions.setSuccess(`booking updated`))
    }
    return true
  }, {
    throwErrors: true,
  }),

  deleteBooking: ({
    booking_id,
    bookingform,
  }) => wrapper('deleteBooking', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.delete(`/admin/projects/${project}/bookingforms/${bookingform}/bookings/${booking_id}`)
    dispatch(snackbarActions.setSuccess(`booking deleted`))
    return true
  }, {
    throwErrors: true,
  }),

  loadEmailAttachments: () => wrapper('loadEmailAttachments', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    const data = await handlers.get(`/admin/projects/${project}/filestore/email_attachments`)
    dispatch(actions.setEmailAttachments(data))
    return true
  }, {
    throwErrors: true,
  }),

  createEmailAttachment: ({
    data,
  }) => wrapper('createEmailAttachment', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.post(`/admin/projects/${project}/filestore/email_attachments`, data)
    await dispatch(actions.loadEmailAttachments())
    dispatch(snackbarActions.setSuccess(`attachment added`))
    return true
  }, {
    throwErrors: true,
  }),

  updateEmailAttachment: ({
    id,
    data,
  }) => wrapper('updateEmailAttachment', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.put(`/admin/projects/${project}/filestore/email_attachments/${id}`, data)
    await dispatch(actions.loadEmailAttachments())
    dispatch(snackbarActions.setSuccess(`attachment updated`))
    return true
  }, {
    globalLoading: true,
    throwErrors: true,
  }),

  deleteEmailAttachment: ({
    id,
  }) => wrapper('deleteEmailAttachment', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    await handlers.delete(`/admin/projects/${project}/filestore/email_attachments/${id}`)
    await dispatch(actions.loadEmailAttachments())
    dispatch(snackbarActions.setSuccess(`attachment deleted`))
    return true
  }, {
    globalLoading: true,
    throwErrors: true,
  }),

  search: ({
    id,
    query,
  }) => wrapper('search', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    const results = await handlers.post(`/admin/projects/${project}/bookingforms/${id}/search`, {
      query,
    })
    await dispatch(actions.setSearchResults(results))
    return true
  }, {
    globalLoading: false,
    throwErrors: true,
  }),

  openSearch: ({
    project,
    bookingform,
    id,
  }) => wrapper('openSearch', async (dispatch, getState) => {
    const result = await handlers.get(`/admin/projects/${project}/bookingforms/${bookingform}/bookings/${id}`)
    dispatch(routerActions.navigateTo('admin.bookingforms.edit.booking', {
      id: result.bookingform,
      date: result.date.split('T')[0],
      block_id: result.block_id,
      slot_id: result.slot_id,
      booking_id: result.id,
    }))
  }, {
    globalLoading: false,
    throwErrors: true,
  }),

  moveBooking: ({
    bookingform,
    id,
    data,
  }) => wrapper('moveBooking', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    const result = await handlers.put(`/admin/projects/${project}/bookingforms/${bookingform}/bookings/${id}`, data)
    dispatch(snackbarActions.setSuccess(`booking moved`))
    return true
  }, {
    globalLoading: true,
    throwErrors: true,
  }),

  loadMoveCalendar: ({
    project,
    bookingform,
    date,
    condensed,
    days = 3,
  }) => wrapper('loadMoveCalendar', async (dispatch, getState) => {
    const data = await handlers.get(`/booking/calendar/${project}/${bookingform}/${date}`, null, {
      params: {
        days,
        condensed: condensed ? 'true' : 'false',
        admin: 'true',
      },
    })
    const calendar = calendarUtils.explodeCalendar(data.calendar, data.slotCache)
    dispatch(actions.setMoveCalendar(calendar))
    return calendar
  }, {
    globalLoading: false,
  }),

  loadMoveCalendarConfig: ({
    project,
    bookingform,
  }) => wrapper('loadMoveCalendarConfig', async (dispatch, getState) => {
    const data = await handlers.get(`/booking/config/${project}/${bookingform}`)    
    dispatch(actions.setMoveCalendarConfig(data))
    return data
  }, {
    globalLoading: false,
  }),

  connectStripe: () => wrapper('connectStripe', async (dispatch, getState) => {
    const project = projectSelectors.currentProjectId(getState())
    const data = await handlers.post(`/admin/projects/${project}/stripe/connect/initiate`, {
      finalize_url: document.location.href.split('?')[0],
    })
    document.location = data.url
    await bluebird.delay(10000)
  }, {
    globalLoading: true,
  }),

}

const reducer = CreateReducer({
  initialState,
  reducers,
  prefix,
})

const actions = CreateActions({
  reducers,
  sideEffects,
  prefix,
})

export { actions, reducer }
export default actions